import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Chip, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import RetryNotice from './RetryNotice';
import { departmentLabel } from './departments';
import WarningLoading from './WarningLoading';
import FlagSummary from './FlagSummary';
import apiRequest from '../../api/request';
import WarningMetricTable from './WarningMetricTable';
import useConfigAccess from './useConfigAccess';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

const StatusCell = ({ isWarning }) =>
  isWarning ? (
    <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />
  ) : (
    <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />
  );

const ThresholdCell = ({ threshold, slabStatus }) => {
  if (slabStatus === 'no_slabs_for_department') {
    return <Chip size="small" color="warning" variant="outlined" label="No slab for this month" title="No slab is in force for this department in the selected month. Add one in Slab Configuration and set 'Applies from' to this month or earlier." />;
  }
  if (slabStatus === 'gap') {
    return <Chip size="small" color="warning" variant="outlined" label="Target is in no slab" title="This person's target falls outside every slab set for this department in the selected month - add or widen a slab." />;
  }
  return threshold === null
    ? <Typography variant="body2" color="text.secondary">Not configured</Typography>
    : `< ₹${Number(threshold).toLocaleString()}`;
};

// ARPU results for the selected month - Revenue ÷ Admissions, an amount
// per student, not a percentage. Zero admissions makes ARPU undefined
// (shown as "-", never a warning) - a zero-admission warning belongs to
// the Admission Count dashboard instead (see arpu_dashboard.controller.js).
const ArpuDashboard = () => {
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalEvaluated: 0, warningCount: 0, needsSlabConfigCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `warning-dashboard/arpu-dashboard?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this check a server error
        // rendered as an empty page (or zeros) instead of a message
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load ARPU results');
          return;
        }
        setRows(res?.data?.rows || []);
        setSummary({
          totalEvaluated: res?.data?.totalEvaluated || 0,
          warningCount: res?.data?.warningCount || 0,
          needsSlabConfigCount: res?.data?.needsSlabConfigCount || 0,
        });
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load ARPU results');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  const dangerScore = (row) => (row.value === null || row.threshold === null ? -Infinity : row.threshold - row.value);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => dangerScore(b) - dangerScore(a)),
    [rows]
  );

  const thresholdCsv = (row) => {
    if (row.slabStatus === 'no_slabs_for_department') return 'No slab for this month';
    if (row.slabStatus === 'gap') return 'Target is in no slab';
    return row.threshold === null ? 'Not configured' : `< ₹${Number(row.threshold).toLocaleString()}`;
  };

  const columns = [
    { field: 'status', headerName: 'Status', align: 'center', render: (row) => <StatusCell isWarning={row.isWarning} />, csvValue: (row) => (row.isWarning ? 'Warning' : 'OK') },
    {
      field: 'entityName',
      headerName: 'Name',
      render: (row) => (
        <Typography
          variant="body2"
          fontStyle={row.isActive === false ? 'italic' : 'normal'}
          color={row.isActive === false || row.isWarning ? 'error.main' : 'text.primary'}
        >
          {row.entityName}
          {row.isActive === false && ' (Inactive)'}
        </Typography>
      ),
    },
    { field: 'departmentId', headerName: 'Department', render: (row) => departmentName(row.departmentId), csvValue: (row) => departmentName(row.departmentId) },
    { field: 'revenue', headerName: 'Revenue', align: 'right', render: (row) => `₹${Number(row.revenue).toLocaleString()}`, csvValue: (row) => row.revenue },
    { field: 'admissionsCount', headerName: 'Admissions', align: 'right' },
    {
      field: 'value',
      headerName: 'ARPU',
      align: 'right',
      render: (row) =>
        row.value === null ? (
          <Typography variant="body2" color="text.secondary">-</Typography>
        ) : (
          <Typography variant="body2" fontWeight={row.isWarning ? 700 : 400} color={row.isWarning ? 'error.main' : 'text.primary'}>
            ₹{Number(row.value).toLocaleString()}
          </Typography>
        ),
      csvValue: (row) => (row.value === null ? '-' : row.value),
    },
    {
      field: 'threshold',
      headerName: 'Threshold',
      align: 'right',
      render: (row) => <ThresholdCell threshold={row.threshold} slabStatus={row.slabStatus} />,
      csvValue: thresholdCsv,
    },
  ];

  const { canConfigure } = useConfigAccess();

  return (
    <WarningPage
      title="ARPU - Average Revenue Per User"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">
            Back to Warning Dashboard
          </Button>
          {canConfigure('target-slab-config') && (
            <Button component={Link} to={`/dashboard/warning-dashboard/target-slab-config?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
              Configure Slabs
            </Button>
          )}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && (
        <FlagSummary warningCount={summary.warningCount} totalEvaluated={summary.totalEvaluated}>
            {summary.needsSlabConfigCount > 0 && ` · ${summary.needsSlabConfigCount} have no matching slab`}
          </FlagSummary>
      )}

      {!loading && !error && (
        <WarningMetricTable
          rows={sortedRows}
          columns={columns}
          departmentName={departmentName}
          emptyMessage="No one has a Target Revenue set for this period."
          exportFileName="ARPU"
        />
      )}
    </WarningPage>
  );
};

export default ArpuDashboard;
